/**
 * Registro de Cargos — migración web de ares5060.
 *
 * Registra un cargo (costo/comisión de gestión de cobro, internamente
 * "llamada") sobre un préstamo activo. Regla del escritorio: un préstamo
 * puede tener a lo sumo UN cargo activo — el backend lo valida al cargar
 * y al guardar. La cancelación del cargo corresponde a ares5080.
 */
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";

const filtrarPrestamos = createFilterOptions({
  stringify: (o) =>
    typeof o === "string"
      ? o
      : `${o.noprest} ${o.nombres || ""} ${o.cedula || ""} ${o.chasis || ""}`,
  limit: 60,
});

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

// Recibo del cargo (impresión con iframe oculto).
function imprimirRecibo(recibo) {
  const fila = (k, v) =>
    `<tr><td style="padding:3px 8px;color:#555">${k}</td><td style="padding:3px 8px;font-weight:bold">${
      v || ""
    }</td></tr>`;
  const html = `
    <html><head><meta name="color-scheme" content="light"><title>Registro de Cargo</title></head>
    <body style="background:#fff;color:#000;font-family:Arial,sans-serif;padding:18px;font-size:13px">
      <p style="text-align:center;margin:0 0 14px;font-weight:bold">Registro de Cargo</p>
      <table style="width:100%;border-collapse:collapse">
        ${fila("Fecha", recibo.fecha)}
        ${fila("Préstamo", recibo.noprest)}
        ${fila("Cliente", recibo.cliente)}
        ${fila("Cédula", recibo.cedula)}
        ${fila("Razón", recibo.razon)}
        ${fila("Valor", "RD$ " + fmtMoney(recibo.valor))}
      </table>
      <p style="margin-top:34px;border-top:1px solid #000;padding-top:6px;text-align:center">Firma</p>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      /* noop */
    }
    setTimeout(() => iframe.remove(), 60000);
  }, 200);
}

export default function RegistrarCargo({ embedded = false, onExit, onSaved, noprestInicial } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const hoy = new Date().toLocaleDateString("es-DO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  // ---- Préstamos activos (carga completa, filtro local) ----
  const [opts, setOpts] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [prestamoSel, setPrestamoSel] = useState(null);

  const cargarPrestamos = () => {
    setBuscando(true);
    return post("/api/cargo_prestamos", {})
      .then(({ ok, data }) => {
        if (ok && Array.isArray(data.prestamos)) setOpts(data.prestamos);
      })
      .finally(() => setBuscando(false));
  };

  useEffect(() => {
    cargarPrestamos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Datos del préstamo ----
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [razon, setRazon] = useState("");
  const [valor, setValor] = useState("");

  // Preselección: al abrir desde el expediente ya viene el préstamo elegido.
  const [preselListo, setPreselListo] = useState(false);
  useEffect(() => {
    if (!noprestInicial || preselListo || !opts.length) return;
    const opcion = opts.find((x) => String(x.noprest) === String(noprestInicial));
    if (opcion) { setPrestamoSel(opcion); cargarDatos(opcion.noprest); };
    setPreselListo(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts]);

  const cargarDatos = async (noprest) => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/cargo_datos", { noprest: String(noprest) });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo cargar el préstamo", color: "error" });
        setPrestamoSel(null);
        setDatos(null);
        return;
      }
      setDatos(data);
    } finally {
      setCargando(false);
    }
  };

  // ---- Guardar ----
  const [guardando, setGuardando] = useState(false);

  const limpiar = () => {
    setPrestamoSel(null);
    setDatos(null);
    setRazon("");
    setValor("");
  };

  const guardar = async () => {
    if (!prestamoSel?.noprest || !datos) {
      showAlert({ message: "Seleccione un préstamo", color: "warning" });
      return;
    }
    if (toNum(valor) <= 0) {
      showAlert({ message: "El valor no puede estar en 0", color: "warning" });
      return;
    }
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/cargo_guardar", {
        noprest: String(prestamoSel.noprest),
        razon: razon.trim(),
        valor: toNum(valor),
      });
      if (ok) {
        showAlert({ message: data.mensajeok || "Datos grabados correctamente", color: "success" });
        if (data.recibo) imprimirRecibo(data.recibo);
        limpiar();
        if (onSaved) onSaved();
      } else {
        showAlert({ message: data.Error || "Error al guardar", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Registro de Cargos"
        subtitle="Registra un cargo de gestión de cobro sobre un préstamo activo (máximo un cargo activo por préstamo)"
        cta={
          <Stack direction="row" spacing={1.2}>
            {embedded && (
              <Button startIcon={<ArrowBackIcon />} onClick={onExit} sx={btnOutline(FP.color.navy)}>
                Volver
              </Button>
            )}
            <Button startIcon={<RestartAltIcon />} onClick={limpiar} sx={btnOutline(FP.color.navy)}>
              Nuevo
            </Button>
            <Button
              startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={guardar}
              disabled={guardando || !datos}
              sx={btnSolid(FP.color.navy, FP.color.navySoft)}
            >
              Guardar
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
          <Chip
            label={`Fecha: ${hoy}`}
            sx={{ bgcolor: FP.color.surfaceAlt, color: FP.color.textSecondary, fontWeight: 700 }}
          />
        </Stack>

        {/* Préstamo */}
        <Box sx={sectionBox}>
          <SectionTitle icon={PersonOutlineIcon} color={FP.color.info}>
            Préstamo
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={5}>
              <Autocomplete
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarPrestamos}
                options={opts}
                loading={buscando || cargando}
                value={prestamoSel}
                getOptionLabel={(o) =>
                  typeof o === "string" ? o : `${o.noprest} — ${o.nombres || ""}`
                }
                isOptionEqualToValue={(o, v) => (o.noprest || o) === (v.noprest || v)}
                onChange={(_, v) => {
                  setPrestamoSel(v);
                  if (v?.noprest) cargarDatos(v.noprest);
                  else setDatos(null);
                }}
                renderOption={(props, o) => (
                  <li {...props} key={o.noprest}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        {o.noprest}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {[o.nombres, o.cedula, o.chasis].filter(Boolean).join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="No. de préstamo, cliente o chasis" sx={fpInputSx} />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Cédula"
                value={datos?.cedula || ""}
                InputProps={{ readOnly: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Cliente"
                value={datos ? [datos.nombres, datos.apellidos].filter(Boolean).join(" ") : ""}
                InputProps={{ readOnly: true }}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Cargo */}
        <Box sx={{ ...sectionBox, mb: 0 }}>
          <SectionTitle icon={PaymentsOutlinedIcon} color={FP.color.success}>
            Cargo
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Razón / motivo del cargo"
                value={razon}
                onChange={(e) => setRazon(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Valor del cargo"
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                }}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
}
